'use client';

import Link from '~/(tenant)/_components/tenant-link';
import { useState } from 'react';

import { Menu, Home, Calendar, Package, IdCard, ChevronRight, CalendarPlus, LogIn, Settings, LogOut } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { NavigationMenu, NavigationMenuList } from '@kit/ui/navigation-menu';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@kit/ui/sheet';
import { Trans } from '@kit/ui/trans';
import { useSignOut } from '@kit/supabase/hooks/use-sign-out';

import type {
  LandingNavLink,
  NavTabsConfig,
} from '~/(tenant)/_lib/schema/landing-config.schema';

import {
  authRequiredLinks,
  authTabLabelOverride,
  orderedVisibleAuthTabs,
} from './_lib/auth-nav-tabs';

import {
  isInternalHashLink,
  isPlainClick,
  resolveSamePageHash,
  scrollToHashTarget,
} from './_lib/hash-scroll';
import { pickLocalized } from './native/_lib/use-localized';
import { SiteNavigationItem } from './site-navigation-item';
import { useSession } from '~/lib/hooks/use-header-auth';
import type { WebBranding } from '~/lib/tenant/web-branding.schema';
import { cn } from '@kit/ui/utils';

import { brandingChromeStyle } from './_lib/web-branding-style';
import styles from './web-branding.module.css';

export function SiteAuthenticatedNavigation({ tabs }: { tabs?: NavTabsConfig }) {
  const session = useSession();
  const { t, i18n } = useTranslation();
  const visibleTabs = orderedVisibleAuthTabs(tabs);

  if (!session.data || visibleTabs.length === 0) return null;

  return (
    <nav aria-label={t('marketing:sportsChrome.memberNavigation')} className="flex flex-wrap justify-center gap-x-6 gap-y-2 border-t border-white/15 px-4 py-3 text-sm">
      {visibleTabs.map((key) => {
        const item = authRequiredLinks[key];
        return <Link key={key} href={item.path} className="hover:underline">{authTabLabelOverride(key, tabs, i18n.language) ?? t(item.i18nKey)}</Link>;
      })}
    </nav>
  );
}

export function SiteNavigation({
  customLinks,
  tabs,
  appearance,
}: {
  appearance?: WebBranding['header'];
  /**
   * Website-builder marketing links (visible to everyone, including
   * anonymous visitors) — configured at Settings → Website → Navigation.
   */
  customLinks?: LandingNavLink[];
  /**
   * Built-in signed-in tabs config (hide flags, label overrides, order) for
   * Bookings / My Packages / My Memberships. Absent = all shown, default
   * labels, default order.
   */
  tabs?: NavTabsConfig;
}) {
  const session = useSession();
  const isAuthenticated = !!session.data;
  const { i18n } = useTranslation();

  // Tenant-configured marketing links, shown to every visitor.
  const CustomNavItems = (customLinks ?? []).map((link) => (
    <SiteNavigationItem key={`custom-${link.href}-${link.label}`} path={link.href}>
      {pickLocalized(link.label, link.label_ar, i18n.language) ?? link.label}
    </SiteNavigationItem>
  ));

  // Authenticated page nav items in the tenant's configured order, dropping any
  // hidden tab and applying label overrides (e.g. "Bookings" → "My Bookings").
  const AuthNavItems = isAuthenticated
    ? orderedVisibleAuthTabs(tabs).map((key) => {
        const item = authRequiredLinks[key];
        const override = authTabLabelOverride(key, tabs, i18n.language);
        return (
          <SiteNavigationItem key={item.path} path={item.path}>
            {override ?? <Trans i18nKey={item.i18nKey} defaults={item.label} />}
          </SiteNavigationItem>
        );
      })
    : [];

  return (
    <>
      <div className={'hidden items-center justify-center md:flex'}>
        <NavigationMenu>
          <NavigationMenuList className="gap-x-6">
            {CustomNavItems}
            {AuthNavItems}
          </NavigationMenuList>
        </NavigationMenu>
      </div>

      <div className={'flex justify-start sm:items-center md:hidden'}>
        <MobileSidebar
          isAuthenticated={isAuthenticated}
          customLinks={customLinks}
          tabs={tabs}
          appearance={appearance}
        />
      </div>
    </>
  );
}

function MobileSidebar({
  isAuthenticated,
  customLinks,
  tabs,
  appearance,
}: {
  appearance?: WebBranding['header'];
  isAuthenticated: boolean;
  customLinks?: LandingNavLink[];
  tabs?: NavTabsConfig;
}) {
  const [open, setOpen] = useState(false);
  // A same-page section tap sets this, then closes the sheet; the scroll is
  // deferred to the sheet's onCloseAutoFocus so it runs after the close
  // animation releases the body scroll-lock (scrollIntoView is a no-op while
  // it's held on mobile) and after Radix would restore focus to the trigger.
  const [pendingScrollId, setPendingScrollId] = useState<string | null>(null);
  const signOut = useSignOut();
  const { t, i18n } = useTranslation();
  const menuStyle = brandingChromeStyle(appearance, true);

  const handleLinkClick = () => {
    setOpen(false);
  };

  const handleSignOut = async () => {
    setOpen(false);
    await signOut.mutateAsync();
  };

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <button
        onClick={() => setOpen(true)}
        aria-label={t('common:memberClub.nav.openMenuAriaLabel', { defaultValue: 'Open Menu' })}
        className="flex items-center justify-center p-2 -ml-2"
      >
        <Menu className="h-6 w-6" />
      </button>

      <SheetContent
        side="left"
        className={cn('w-[280px] p-0', menuStyle?.color && styles.menu)}
        style={menuStyle}
        onCloseAutoFocus={(event) => {
          // Fires after the close animation + body scroll-lock release, and is
          // where Radix restores focus to the hamburger. If a same-page section
          // tap is pending, preventDefault stops that restore so we can place
          // focus in the target section instead — no focus-steal race, and no
          // magic-number timer guessing the animation length.
          if (!pendingScrollId) return;
          event.preventDefault();
          const id = pendingScrollId;
          setPendingScrollId(null);
          scrollToHashTarget(id);
        }}
      >
        <SheetHeader className="p-6 pb-4 border-b">
          <SheetTitle className="text-left text-lg font-semibold">
            <Trans i18nKey="common:memberClub.nav.menu" defaults="Menu" />
          </SheetTitle>
        </SheetHeader>

        <nav className="flex flex-col py-4">
          {/* Book Now - Primary action */}
          <Link
            href="/book"
            onClick={handleLinkClick}
            className="flex items-center gap-3 mx-4 mb-4 px-4 py-3 text-base font-semibold bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors"
          >
            <CalendarPlus className="h-5 w-5" />
            <span><Trans i18nKey="common:memberClub.nav.bookNow" defaults="Book Now" /></span>
          </Link>

          <div className="mb-2 mx-6 border-t" />

          {/* Home link */}
          <Link
            href="/"
            onClick={handleLinkClick}
            className="flex items-center gap-3 px-6 py-3 text-base font-medium hover:bg-secondary/20 transition-colors"
          >
            <Home className="h-5 w-5 text-secondary" />
            <span><Trans i18nKey="common:memberClub.nav.home" defaults="Home" /></span>
            <ChevronRight className="h-4 w-4 ms-auto text-muted-foreground" />
          </Link>

          {/* Tenant-configured marketing links (website builder) */}
          {(customLinks ?? []).map((link) => {
            const label =
              pickLocalized(link.label, link.label_ar, i18n.language) ??
              link.label;
            const isExternal = /^https?:\/\//i.test(link.href);
            // Any app-internal URL with a fragment renders as a scrollable <a>;
            // the click handler below decides same-document scroll vs native
            // navigation. External (https://…/#x) links keep their target/rel.
            const isHash = isInternalHashLink(link.href);
            const className =
              'flex items-center gap-3 px-6 py-3 text-base font-medium hover:bg-secondary/20 transition-colors';
            const inner = (
              <>
                <span>{label}</span>
                <ChevronRight className="h-4 w-4 ms-auto text-muted-foreground" />
              </>
            );

            // Same-page section anchors render as a Next <Link> (keeps
            // client-side routing + prefetch when tapped from another route);
            // a genuine same-page tap is intercepted to smooth-scroll instead
            // of route-navigating (which lands at the top).
            if (isHash) {
              return (
                <Link
                  key={`custom-${link.href}-${link.label}`}
                  href={link.href}
                  onClick={(e) => {
                    // Let Cmd/Ctrl/middle clicks open normally and keep the
                    // sheet open.
                    if (!isPlainClick(e)) return;
                    const id = resolveSamePageHash(link.href);
                    const target =
                      id && typeof document !== 'undefined'
                        ? document.getElementById(id)
                        : null;
                    // No same-page target — a cross-route link (tapped from
                    // /book), or the section isn't on this page. Close the sheet
                    // and let <Link> soft-navigate (client-side, keeps prefetch)
                    // rather than a full document reload.
                    if (!id || !target) {
                      handleLinkClick();
                      return;
                    }
                    // Same-page section: suppress <Link>'s navigation and defer
                    // the scroll to the sheet's onCloseAutoFocus, which runs once
                    // the close animation releases the body scroll-lock.
                    e.preventDefault();
                    setPendingScrollId(id);
                    setOpen(false);
                  }}
                  className={className}
                >
                  {inner}
                </Link>
              );
            }

            return isExternal ? (
              <a
                key={`custom-${link.href}-${link.label}`}
                href={link.href}
                target="_blank"
                rel="noopener noreferrer"
                onClick={handleLinkClick}
                className={className}
              >
                {inner}
              </a>
            ) : (
              <Link
                key={`custom-${link.href}-${link.label}`}
                href={link.href}
                onClick={handleLinkClick}
                className={className}
              >
                {inner}
              </Link>
            );
          })}

          {/* Auth-required page links */}
          {isAuthenticated && (
            <>
              <div className="my-2 mx-6 border-t" />
              {orderedVisibleAuthTabs(tabs).map((key) => {
                const item = authRequiredLinks[key];
                const override = authTabLabelOverride(key, tabs, i18n.language);
                const Icon =
                  key === 'Bookings'
                    ? Calendar
                    : key === 'Memberships'
                      ? IdCard
                      : Package;
                return (
                  <Link
                    key={item.path}
                    href={item.path}
                    onClick={handleLinkClick}
                    className="flex items-center gap-3 px-6 py-3 text-base font-medium hover:bg-secondary/20 transition-colors"
                  >
                    <Icon className="h-5 w-5 text-secondary" />
                    <span>
                      {override ?? (
                        <Trans i18nKey={item.i18nKey} defaults={item.label} />
                      )}
                    </span>
                    <ChevronRight className="h-4 w-4 ms-auto text-muted-foreground" />
                  </Link>
                );
              })}

              {/* Profile Settings */}
              <Link
                href="/profile"
                onClick={handleLinkClick}
                className="flex items-center gap-3 px-6 py-3 text-base font-medium hover:bg-secondary/20 transition-colors"
              >
                <Settings className="h-5 w-5 text-secondary" />
                <span><Trans i18nKey="common:memberClub.nav.profileSettings" defaults="Profile Settings" /></span>
                <ChevronRight className="h-4 w-4 ms-auto text-muted-foreground" />
              </Link>

              <div className="my-2 mx-6 border-t" />

              {/* Sign Out */}
              <button
                onClick={handleSignOut}
                className="flex items-center gap-3 px-6 py-3 text-base font-medium hover:bg-muted transition-colors text-left w-full text-destructive"
              >
                <LogOut className="h-5 w-5" />
                <span><Trans i18nKey="auth:signOut" defaults="Sign out" /></span>
              </button>
            </>
          )}

          {/* Sign In - only show if not authenticated */}
          {!isAuthenticated && (
            <>
              <div className="my-2 mx-6 border-t" />
              <Link
                href="/auth/sign-in"
                onClick={handleLinkClick}
                className="flex items-center gap-3 px-6 py-3 text-base font-medium hover:bg-secondary/20 transition-colors"
              >
                <LogIn className="h-5 w-5 text-secondary" />
                <span><Trans i18nKey="common:memberClub.nav.signIn" defaults="Sign In" /></span>
                <ChevronRight className="h-4 w-4 ms-auto text-muted-foreground" />
              </Link>
            </>
          )}
        </nav>
      </SheetContent>
    </Sheet>
  );
}
